// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Configuration Panel
 *
 * Renders configuration UI based on node metadata and admin UI settings.
 * Inline layout so node developers only implement their screen component.
 * Fetches and saves node instance data via API when instance ID is available.
 */

import { forwardRef, useEffect, useImperativeHandle, useState, useCallback, useRef } from 'react'
import { Button } from '@/components/ui'
import { Save, Loader2 } from 'lucide-react'
import { getPipelineNode } from '../../../core/pipeline-registry'
import { getLayoutComponent } from '../../../core/layout-registry'
import type { NodeDisplayMetadata } from '../../../core/types'
import type { ComponentType } from 'react'
import type { NodeAdminViewProps } from '../../../core/types'
import { extractInheritance, type InheritanceMetadata } from '../types/inheritance'
import { OverriddenFieldsSummary } from './OverriddenFieldsSummary'

/** Resolved node info for configuration panel - works with both registries */
interface ResolvedNodeConfig {
  display: NodeDisplayMetadata
  adminConfig?: ComponentType<NodeAdminViewProps>
}

/** Look up a node by ID from either pipeline or layout registry */
function resolveNodeForConfig(nodeDefinition: string): ResolvedNodeConfig | null {
  // Check pipeline registry first
  const pipelineNode = getPipelineNode(nodeDefinition)
  if (pipelineNode) {
    return {
      display: pipelineNode.display,
      adminConfig: pipelineNode.AdminConfig,
    }
  }

  // Fall back to layout registry
  const layoutComponent = getLayoutComponent(nodeDefinition)
  if (layoutComponent) {
    return {
      display: layoutComponent.display,
      adminConfig: layoutComponent.ConfigComponent,
    }
  }

  return null
}
import type { PipelineNodeData } from '../../../core/contracts'
import { NodeLabelEditor } from './NodeLabelEditor'
import { getServices } from '@/app-shell/services'
import { toast } from 'sonner'
import type { ConfigurationPanelHandle, ConfigurationPanelProps } from '../types'

export type { ConfigurationPanelHandle }

export const ConfigurationPanel = forwardRef<ConfigurationPanelHandle, ConfigurationPanelProps>(({
  node,
  onClose,
  onUpdate,
  organizationId,
  workspaceId,
  pipelineId,
  agentId,
  onDirtyChange,
}, ref) => {
  const [resolvedNode, setResolvedNode] = useState<ResolvedNodeConfig | null>(null)
  const [configData, setConfigData] = useState<PipelineNodeData>(node.data as PipelineNodeData)
  const [isInitializing, setIsInitializing] = useState(true)
  const [isLoadingInstance, setIsLoadingInstance] = useState(false)
  const [isSavingInstance, setIsSavingInstance] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [inheritance, setInheritance] = useState<InheritanceMetadata | undefined>(undefined)
  const skipNextFetchRef = useRef(false)

  // Stable refs for callbacks to avoid restarting effects when parent re-renders
  const onCloseRef = useRef(onClose)
  const onDirtyChangeRef = useRef(onDirtyChange)
  useEffect(() => { onCloseRef.current = onClose }, [onClose])
  useEffect(() => { onDirtyChangeRef.current = onDirtyChange }, [onDirtyChange])

  // Custom save for components without instanceId (e.g. application config panels)
  const customSaveRef = useRef<(() => Promise<boolean>) | null>(null)
  const registerSave = useCallback((fn: () => Promise<boolean>) => {
    customSaveRef.current = fn
  }, [])

  useEffect(() => {
    const nodeDefinition = (node.data as PipelineNodeData)?.nodeDefinition
    if (nodeDefinition && typeof nodeDefinition === 'string') {
      const nodeDef = resolveNodeForConfig(nodeDefinition)
      setResolvedNode(nodeDef)
    } else {
      setResolvedNode(null)
    }
  }, [node.data])

  useEffect(() => {
    setConfigData(node.data as PipelineNodeData)
    setIsInitializing(true)
    setInheritance(undefined)
    setIsDirty(false)
    onDirtyChangeRef.current?.(false)
    customSaveRef.current = null

    // Allow nodes to initialize (e.g., auto-select defaults) without marking as dirty
    const timer = setTimeout(() => {
      setIsInitializing(false)
    }, 500)

    return () => clearTimeout(timer)
  }, [node.data, node.id, organizationId, workspaceId, pipelineId])

  // Fetch node instance data from API when panel opens (if instance ID exists)
  useEffect(() => {
    const loadNodeInstanceData = async () => {
      // Skip refetch if we just saved (already have updated data)
      if (skipNextFetchRef.current) {
        skipNextFetchRef.current = false
        return
      }

      if (!organizationId || !workspaceId || !pipelineId) {
        return
      }

      const nodeData = node.data as PipelineNodeData
      const instanceId = nodeData?.instanceId

      if (!instanceId) {
        return
      }

      setIsLoadingInstance(true)
      try {
        const services = getServices()
        const instance = await services.nodeInstance.getNodeInstance(
          organizationId,
          workspaceId,
          pipelineId,
          instanceId,
          { agentId }
        )

        // Replace config with API response (not merge) - ensures form shows resolved values
        setConfigData(prev => ({
          ...prev,
          label: instance.label ?? prev.label,
          config: instance.settings,
        }))

        // Extract inheritance metadata from backend (single source of truth)
        const inheritanceData = extractInheritance(instance.runtime)
        setInheritance(inheritanceData)
      } catch {
        // Instance may not exist yet (e.g., new node) - this is expected
      } finally {
        setIsLoadingInstance(false)
      }
    }

    void loadNodeInstanceData()
  }, [node.id, node.data, organizationId, workspaceId, agentId])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => {
      window.removeEventListener('keydown', handleEscape)
    }
  }, [])

  // Explicit handlers for each data destination - avoids hidden branching logic
  const setLabel = (label: string) => {
    setConfigData(prev => ({ ...prev, label }))
    if (!isInitializing) {
      setIsDirty(true)
      onDirtyChange?.(true)
    }
  }

  const setConfigValue = (key: string, value: unknown) => {
    setConfigData(prev => {
      const prevConfig = (prev.config as Record<string, unknown>) ?? {}
      return {
        ...prev,
        config: {
          ...prevConfig,
          [key]: value,
        },
      } as PipelineNodeData
    })
    if (!isInitializing) {
      setIsDirty(true)
      onDirtyChange?.(true)
    }
  }

  const mergeConfig = (updates: Record<string, unknown>) => {
    setConfigData(prev => {
      const { config: updateConfig, ...topLevel } = updates
      const prevConfig = (prev.config as Record<string, unknown>) ?? {}
      const mergedConfig = updateConfig && typeof updateConfig === 'object'
        ? { ...prevConfig, ...(updateConfig as Record<string, unknown>) }
        : prevConfig

      // Filter out undefined values from top-level updates
      const definedUpdates = Object.fromEntries(
        Object.entries(topLevel).filter(([, v]) => v !== undefined)
      )
      return { ...prev, ...definedUpdates, config: mergedConfig }
    })
    if (!isInitializing) {
      setIsDirty(true)
      onDirtyChange?.(true)
    }
  }

  // Legacy adapter for node screens that use onChange(key, value) signature
  const handleConfigChange = (key: string, value: unknown) => {
    if (key === 'config' && value && typeof value === 'object') {
      mergeConfig(value as Record<string, unknown>)
    } else {
      setConfigValue(key, value)
    }
  }

  // Clear an overridden field to reset to inherited value
  const handleClearOverride = useCallback(async (fieldPath: string) => {
    if (!inheritance || !organizationId || !workspaceId || !pipelineId) return

    const nodeData = node.data as PipelineNodeData
    const instanceId = nodeData?.instanceId

    // Check if this is the last overridden field
    const remainingFields = inheritance.overriddenFields.filter(f => f !== fieldPath)
    const isLastField = remainingFields.length === 0

    if (isLastField && instanceId) {
      // Delete user workspace node to fully reset to inherited
      try {
        const services = getServices()
        await services.nodeConfig.deleteNodeInstance(
          organizationId,
          workspaceId,
          pipelineId,
          instanceId,
          { agentId }
        )

        // Refetch to get inherited values from app workspace
        const instance = await services.nodeInstance.getNodeInstance(
          organizationId,
          workspaceId,
          pipelineId,
          instanceId,
          { agentId }
        )

        // Update config with inherited values (replace entirely, don't merge)
        const updatedConfigData = {
          ...configData,
          label: instance.label ?? configData.label,
          config: instance.settings,
        }
        setConfigData(updatedConfigData)

        // Update inheritance from backend (single source of truth)
        const inheritanceData = extractInheritance(instance.runtime)
        setInheritance(inheritanceData)

        // Skip refetch and update node data
        skipNextFetchRef.current = true
        onUpdate(updatedConfigData)
        setIsDirty(false)
        onDirtyChangeRef.current?.(false)

        // Close panel after successful reset (it's a complete action like Save)
        onCloseRef.current()
      } catch (error) {
        console.error('[ConfigPanel] Failed to reset to inherited:', error)
        toast.error('Failed to reset to inherited values')
      }
    } else {
      // Just update local state for partial reset (user still needs to save)
      const parts = fieldPath.split('.')
      if (parts[0] === 'settings' && parts.length > 1) {
        const settingsKey = parts.slice(1).join('.')
        setConfigData(prev => {
          const prevConfig = (prev.config as Record<string, unknown>) ?? {}
          const newConfig = { ...prevConfig }
          delete newConfig[settingsKey]
          return { ...prev, config: newConfig }
        })
      } else if (fieldPath === 'label') {
        setConfigData(prev => ({ ...prev, label: '' }))
      }

      setInheritance(prev => {
        if (!prev) return prev
        return {
          ...prev,
          overriddenFields: remainingFields,
        }
      })

      if (!isInitializing) {
        setIsDirty(true)
        onDirtyChangeRef.current?.(true)
      }
    }
  }, [inheritance, organizationId, workspaceId, pipelineId, agentId, node.data, configData, isInitializing, onUpdate])

  const handleSaveToAPI = useCallback(async () => {
    if (!isDirty) {
      return true
    }

    if (!organizationId || !workspaceId) {
      toast.error('Missing organization or workspace context')
      return false
    }

    const instanceId = configData?.instanceId

    if (!pipelineId || !instanceId) {
      if (customSaveRef.current) {
        setIsSavingInstance(true)
        try {
          const success = await customSaveRef.current()
          if (success) {
            onUpdate(configData)
            setIsDirty(false)
            onDirtyChangeRef.current?.(false)
            onCloseRef.current()
          }
          return success
        } catch (error) {
          console.error('[ConfigPanel] Custom save failed:', error)
          toast.error('Failed to save configuration')
          return false
        } finally {
          setIsSavingInstance(false)
        }
      }
      // No custom save — local-only update
      onUpdate(configData)
      setIsDirty(false)
      onDirtyChangeRef.current?.(false)
      onCloseRef.current()
      toast.info('Configuration saved locally. Save the pipeline to persist.')
      return true
    }

    setIsSavingInstance(true)
    try {
      const services = getServices()
      const config = (configData.config as Record<string, unknown>) ?? {}

      // Separate settings from data
      const { data: nodePayloadData, ...configSettings } = config
      const nodeDefinition = configData.nodeDefinition
      if (typeof nodeDefinition !== 'string' || !nodeDefinition) {
        throw new Error('Pipeline node definition is required to save an instance')
      }
      // cm-coding: The first settings save can occur before a separate settings
      // record exists for this graph node. Include the node definition so the
      // API can choose and validate the correct settings model; sending values
      // alone would make the initial save ambiguous or validate them as the
      // wrong node type.
      const instanceSettings = {
        ...configSettings,
        node_definition: nodeDefinition,
      }

      // Estimate payload size (rough approximation)
      const payloadSize = JSON.stringify(nodePayloadData || {}).length
      const MAX_PAYLOAD_SIZE = 350 * 1024 // 350KB threshold for chunking (DynamoDB limit is 400KB)

      let updatedRuntime: Record<string, unknown> | undefined
      if (payloadSize > MAX_PAYLOAD_SIZE && nodePayloadData && !agentId) {
        // Large payloads use chunked endpoint (DynamoDB limit is 400KB)
        const response = await services.nodeInstance.putNodeInstanceData(
          organizationId,
          workspaceId,
          pipelineId!,
          instanceId,
          {
            label: configData.label as string | undefined,
            enabled: true,
            settings: instanceSettings,
            data: nodePayloadData as Record<string, unknown> | undefined,
            sourceFiles: [],
            chunks: [],
          }
        )
        updatedRuntime = response.instance?.runtime
      } else {
        // Use regular endpoint for small payloads
        const instance = await services.nodeInstance.putNodeInstance(
          organizationId,
          workspaceId,
          pipelineId!,
          instanceId,
          {
            label: configData.label as string | undefined,
            enabled: true,
            settings: instanceSettings,
            data: nodePayloadData as Record<string, unknown> | undefined,
          },
          { agentId }
        )
        updatedRuntime = instance?.runtime
      }

      // Update inheritance state from backend response
      if (updatedRuntime) {
        const updatedInheritance = extractInheritance(updatedRuntime)
        setInheritance(updatedInheritance)
      }

      // Skip the refetch triggered by onUpdate changing node.data
      skipNextFetchRef.current = true

      // Also update local state
      onUpdate(configData)
      setIsDirty(false)
      onDirtyChangeRef.current?.(false)

      toast.success('Node saved')
      return true
    } catch (error) {
      console.error('[ConfigPanel] Failed to save node instance:', error)
      toast.error('Failed to save node configuration')
      return false
    } finally {
      setIsSavingInstance(false)
    }
  }, [isDirty, organizationId, workspaceId, pipelineId, agentId, configData, onUpdate])

  const handleSave = () => {
    onUpdate(configData)
    setIsDirty(false)
    onDirtyChangeRef.current?.(false)
    onCloseRef.current()
  }

  useImperativeHandle(ref, () => ({
    save: handleSave,
  }))

  const renderUnavailableState = (message: string) => (
    <div className="flex h-full w-full flex-col rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="space-y-4 text-center">
          <p className="text-[13px] text-slate-500">{message}</p>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  )

  if (!resolvedNode) {
    return renderUnavailableState('Node configuration not available')
  }

  const ConfigComponent = resolvedNode.adminConfig

  // Config prop contains only settings - identity fields (nodeDefinition, instanceId, etc.)
  // are passed as separate props to AdminView components
  const configForComponent = (configData.config as Record<string, unknown>) ?? {}

  return (
    <div
      className="flex h-full w-full flex-col overflow-hidden bg-slate-50"
      data-testid="builder-node-config"
      data-config-ready={String(!isInitializing && !isLoadingInstance)}
    >
      {/* Scrollable content area — width constrained so forms read comfortably */}
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-8 sm:py-8">
        {isLoadingInstance ? (
          <div className="flex items-center justify-center gap-2 py-12">
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            <span className="text-[13px] text-slate-500">Loading configuration…</span>
          </div>
        ) : (
          <div className="mx-auto w-full max-w-3xl space-y-5 animate-in fade-in slide-in-from-bottom-1 duration-200">
            {/* Inheritance summary for nodes extending a base */}
            {inheritance && (
              <OverriddenFieldsSummary
                inheritance={inheritance}
                onClearField={handleClearOverride}
                onSave={handleSaveToAPI}
                isDirty={isDirty}
                disabled={isSavingInstance}
              />
            )}
            {/* Always show instance label editor at top */}
            <div className="rounded-xl border border-[#e7ecf2] bg-white p-5 shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
              <NodeLabelEditor
                label={configData.label as string | undefined}
                onChange={setLabel}
                placeholder={`e.g., ${resolvedNode.display.label} Instance`}
              />
            </div>

            {/* Node-specific configuration */}
            {ConfigComponent ? (
              !organizationId || !workspaceId ? (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
                  Workspace context is required to configure this node. Please select an organization and workspace.
                </div>
              ) : (
                <ConfigComponent
                  orgId={organizationId}
                  workspaceId={workspaceId}
                  agentId={agentId}
                  config={configForComponent}
                  nodeInstanceId={configData?.instanceId}
                  onChange={handleConfigChange}
                  registerSave={registerSave}
                />
              )
            ) : (
              <DefaultConfigForm
                config={configData}
                onChange={handleConfigChange}
                resolvedNode={resolvedNode}
              />
            )}
          </div>
        )}
      </div>

      {/* Fixed action bar - edge-to-edge at bottom */}
      <div className="flex-shrink-0 border-t border-[#eef2f6] bg-white/95 px-4 py-2.5 shadow-[0_-2px_8px_rgba(2,8,23,0.04)] backdrop-blur-sm sm:px-8">
        <div className="mx-auto flex w-full max-w-3xl justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSavingInstance}
            data-testid="builder-config-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveToAPI}
            disabled={isSavingInstance || isLoadingInstance}
            data-testid="builder-config-save"
          >
            {isSavingInstance ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
})

const DefaultConfigForm: React.FC<{
  config: Record<string, unknown>
  onChange: (key: string, value: unknown) => void
  resolvedNode: ResolvedNodeConfig
}> = ({ config, onChange, resolvedNode }) => {
  return (
    <div className="rounded-xl border border-[#e7ecf2] bg-white p-5 shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
      <div className="space-y-2">
        <label htmlFor="node-description" className="block text-sm font-medium text-slate-900">
          Description
        </label>
        <textarea
          id="node-description"
          name="node-description"
          value={(config.description as string) || ''}
          onChange={(e) => onChange('description', e.target.value)}
          className="w-full rounded-md border border-[#e7ecf2] px-3 py-2 text-sm transition-colors duration-150 placeholder:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30"
          rows={3}
          placeholder={resolvedNode.display.description}
        />
        <p className="text-xs text-slate-400">
          This component has no additional settings.
        </p>
      </div>
    </div>
  )
}
